import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, UserPlus } from "lucide-react";
import { toast } from "sonner";

const FULL = "__full__";

function RolePicker({ value, roles, onChange, disabled, testId, allowFull }) {
  return (
    <Select value={value || FULL} disabled={disabled} onValueChange={(v) => onChange(v === FULL ? null : v)}>
      <SelectTrigger className="h-8 w-[220px] text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{allowFull && <SelectItem value={FULL}>HR manager (full access)</SelectItem>}{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.scope === "global" ? " · global" : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function AddMemberDialog({ open, onOpenChange, roles, companyId, onCreated, allowFull }) {
  const [f, setF] = useState({ name: "", email: "", role_id: allowFull ? null : roles[0]?.id || null, send_invite: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!allowFull && !f.role_id && roles[0]) setF((x) => ({ ...x, role_id: roles[0].id })); }, [roles, allowFull, f.role_id]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/roles/team", { ...f, company_id: companyId || undefined });
      onCreated(data); onOpenChange(false); setF({ name: "", email: "", role_id: allowFull ? null : roles[0]?.id || null, send_invite: true });
      if (data.invite_sent) { toast.success(`Login created — welcome email sent to ${data.email}`, { duration: 8000 }); return; }
      await navigator.clipboard?.writeText(data.temp_password).catch(() => {});
      toast.success(`Login created. Temporary password: ${data.temp_password} (copied)`, { duration: 20000 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader><DialogTitle>Add team member</DialogTitle><DialogDescription>Create a login for a coworker and choose what they can access.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div><Label className="text-xs">Full name</Label><Input data-testid="member-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="text-xs">Email</Label><Input data-testid="member-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label className="text-xs">Role</Label><div className="mt-1"><RolePicker value={f.role_id} roles={roles} allowFull={allowFull} onChange={(v) => setF({ ...f, role_id: v })} testId="member-role" /></div></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.send_invite} onCheckedChange={(v) => setF({ ...f, send_invite: !!v })} data-testid="member-send-invite" /> Email a welcome message with the temporary password</label>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="member-submit" disabled={busy || !f.name || !f.email || (!allowFull && !f.role_id)} onClick={submit}><UserPlus className="w-4 h-4 mr-1.5" /> Create login</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TeamAccessCard({ companyId }) {
  const { user } = useAuth();
  const allowFull = !user.permissions;
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const params = companyId ? { company_id: companyId } : {};
  const load = () => Promise.all([api.get("/roles/team", { params }), api.get("/roles", { params })]).then(([t, r]) => { setTeam(t.data); setRoles(r.data); }).catch(() => {});
  useEffect(() => { load(); }, [companyId]); // eslint-disable-line
  const assign = async (u, role_id) => {
    try { const { data } = await api.put(`/roles/team/${u.id}`, { role_id }); setTeam((xs) => xs.map((x) => (x.id === u.id ? data : x))); toast.success(`${u.name || u.email} now has ${data.role_name} — they will sign in again`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="team-access-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="font-display font-semibold text-base md:text-lg flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Team logins</h2><p className="text-xs text-muted-foreground mt-0.5">Everyone with HR-portal access to this company. Changing a role signs that person out so the new access applies immediately.</p></div>
        <Button size="sm" data-testid="add-member-btn" onClick={() => setOpen(true)}><UserPlus className="w-4 h-4 mr-1.5" /> Add team member</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2 pr-3 font-medium">Person</th><th className="pb-2 pr-3 font-medium">Role</th><th className="pb-2 font-medium">Status</th></tr></thead>
          <tbody>
            {team.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`team-row-${u.id}`}>
                <td className="py-2 pr-3"><div className="font-medium">{u.name || "—"}{u.id === user.id && <span className="text-xs text-muted-foreground ml-1">(you)</span>}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="py-2 pr-3">{u.id === user.id ? <Badge variant="outline" data-testid={`team-role-${u.id}`}>{u.role_name}</Badge> : <RolePicker value={u.role_id} roles={roles} allowFull={allowFull || !u.role_id} onChange={(v) => assign(u, v)} testId={`team-role-${u.id}`} />}</td>
                <td className="py-2 text-xs text-muted-foreground">{u.must_change_password ? "Invited · password pending" : "Active"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {team.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="team-empty">No team logins yet.</p>}
      </div>
      <AddMemberDialog open={open} onOpenChange={setOpen} roles={roles} companyId={companyId} allowFull={allowFull} onCreated={(u) => setTeam((xs) => [u, ...xs])} />
    </Card>
  );
}
